import {
  IClinicCopayRule,
  IClinicCopayRuleSaveRequest,
  IClinicCopayRulesView,
} from "types/ClinicCopayRule/ClinicCopayRule";

import { api } from ".";

/**
 * CLE-3050 — the clinic copay rules (Eligibility › Clinic copay rules). SuperAdmin only, every
 * endpoint. Rows are addressed by payer id + CPT group: while nothing is saved the list is the
 * built-in one, whose rows have no id. Every write invalidates the list; the API's own cache is
 * invalidated by the same save.
 *
 * An API without the route answers the GET with 404; the page hides the tab then.
 */
const apiWithClinicCopayRuleTags = api.enhanceEndpoints({
  addTagTypes: ["ClinicCopayRules"],
});

export const CLINIC_COPAY_RULES_PATH = "eligibility/clinic-copay-rules";

const LIST_TAG = { type: "ClinicCopayRules" as const, id: "LIST" };

/** The path of one rule. */
export const clinicCopayRulePath = (
  payerId: number,
  procedureGroup: string
): string =>
  `${CLINIC_COPAY_RULES_PATH}/${encodeURIComponent(
    String(payerId)
  )}/${encodeURIComponent(procedureGroup)}`;

/** True when the error is the API saying the route does not exist (an older API). */
export const isRouteMissing = (error: unknown): boolean =>
  (error as { status?: unknown } | undefined)?.status === 404;

export const clinicCopayRuleQueries =
  apiWithClinicCopayRuleTags.injectEndpoints({
    endpoints: (builder) => ({
      getClinicCopayRules: builder.query<IClinicCopayRulesView, void>({
        query: () => CLINIC_COPAY_RULES_PATH,
        transformResponse: (response: { data: IClinicCopayRulesView }) =>
          response.data,
        providesTags: [LIST_TAG],
      }),
      createClinicCopayRule: builder.mutation<
        IClinicCopayRule,
        IClinicCopayRuleSaveRequest
      >({
        query: (body) => ({
          url: CLINIC_COPAY_RULES_PATH,
          method: "POST",
          body,
        }),
        transformResponse: (response: { data: IClinicCopayRule }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
      updateClinicCopayRule: builder.mutation<
        IClinicCopayRule,
        {
          payerId: number;
          procedureGroup: string;
          body: IClinicCopayRuleSaveRequest;
        }
      >({
        query: ({ payerId, procedureGroup, body }) => ({
          url: clinicCopayRulePath(payerId, procedureGroup),
          method: "PUT",
          body,
        }),
        transformResponse: (response: { data: IClinicCopayRule }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
      deleteClinicCopayRule: builder.mutation<
        unknown,
        { payerId: number; procedureGroup: string }
      >({
        query: ({ payerId, procedureGroup }) => ({
          url: clinicCopayRulePath(payerId, procedureGroup),
          method: "DELETE",
        }),
        transformResponse: (response: { data: unknown }) => response.data,
        invalidatesTags: [LIST_TAG],
      }),
    }),
  });

export const {
  useGetClinicCopayRulesQuery,
  useCreateClinicCopayRuleMutation,
  useUpdateClinicCopayRuleMutation,
  useDeleteClinicCopayRuleMutation,
} = clinicCopayRuleQueries;
